import {
  getAdvertisersByDomainMetrics,
  AdvertisersByDomainWidgetResponse,
  GetAdvertisersByDomainWidgetMetricsArgs,
} from 'api/services';
import { UseQueryOptions, useQuery } from 'react-query';
import { ParsedAdvertisersDomainStatsData } from '../types';
import { promoMetricsQueries } from './queryKeys';
import {
  promoByDomainCompleteStatsDataSelector,
  promoByDomainTopSitesStatsDataSelector,
} from './selectors';

const MAX_TOP_PERFORMING_SITES_DISPLAY_COUNT = 10;

type QueryKey = ReturnType<typeof promoMetricsQueries.daily>;

function useAdvertisersByDomainWidgetMetrics<TData = unknown>(
  args: GetAdvertisersByDomainWidgetMetricsArgs,
  opts?: Omit<
    UseQueryOptions<
      AdvertisersByDomainWidgetResponse,
      unknown,
      TData,
      QueryKey
    >,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<AdvertisersByDomainWidgetResponse, unknown, TData, QueryKey>(
    promoMetricsQueries.domain(args),
    () => getAdvertisersByDomainMetrics(args),
    opts,
  );
}

export function usePromoByDomainCompleteMetrics(
  args: GetAdvertisersByDomainWidgetMetricsArgs,
  opts?: Omit<
    UseQueryOptions<
      AdvertisersByDomainWidgetResponse,
      unknown,
      ParsedAdvertisersDomainStatsData[],
      QueryKey
    >,
    'queryKey' | 'queryFn'
  >,
) {
  return useAdvertisersByDomainWidgetMetrics(args, {
    select: promoByDomainCompleteStatsDataSelector,
    ...opts,
  });
}

export function usePromoByDomainTopSitesMetrics(
  args: GetAdvertisersByDomainWidgetMetricsArgs,
  {
    maxDisplayCount = MAX_TOP_PERFORMING_SITES_DISPLAY_COUNT,
    ...opts
  }: Omit<
    UseQueryOptions<
      AdvertisersByDomainWidgetResponse,
      unknown,
      ParsedAdvertisersDomainStatsData[],
      QueryKey
    >,
    'queryKey' | 'queryFn'
  > & { maxDisplayCount?: number } = {},
) {
  return useAdvertisersByDomainWidgetMetrics(args, {
    select: promoByDomainTopSitesStatsDataSelector(maxDisplayCount),
    ...opts,
  });
}
